// The router's `errorElement` — what a user sees when a route throws.
//
// Until this existed, they saw React Router's DEVELOPER screen: a stack trace
// and the words "💿 Hey developer 👋 You can provide a way better UX than this".
// That is a message to me, rendered to them, in production.
//
// TWO ERRORS, TWO SENTENCES. A chunk that 404s after a deploy is not a bug and
// must not be dressed as one — it is a stale tab, and the fix is a reload, so
// the copy says exactly that. Anything else is a real failure, and the copy
// says THAT, because telling someone "just reload" when reloading will not help
// is how a user learns to distrust every message an app gives them.
//
// `lazyRoute` already reloads once for the first kind, so a chunk error
// reaching this screen means the reload did not fix it — the second failure.
// Hence "try once more" rather than a promise: the honest offer at that point
// is a manual retry and a way out.
//
// NO AUTO-RELOAD HERE. This component is the end of the line for a failure the
// automatic path already gave up on; reloading again from the same screen the
// reload produced is how a loop starts.

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import { useNavigate, useRouteError } from 'react-router-dom';
import { isChunkLoadError } from '../lib/lazyRoute';

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const stale = isChunkLoadError(error);

  return (
    <Box
      role="alert"
      sx={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 4, gap: 1,
      }}
    >
      <ReportProblemOutlined sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
      <Typography variant="h6" fontWeight={700}>
        {stale ? 'This page needs a reload' : "That page didn't load"}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
        {stale
          ? 'QuarterStack updated while this tab was open, so the old version of this page is no longer there. Reloading picks up the new one.'
          : 'Something went wrong opening this page. Your data is fine — nothing was saved or changed.'}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" sx={{ minHeight: 44 }} onClick={() => window.location.reload()}>
          {stale ? 'Reload' : 'Try once more'}
        </Button>
        <Button sx={{ minHeight: 44 }} onClick={() => navigate('/dashboard')}>
          Go to dashboard
        </Button>
      </Stack>

      {/* The message, small and last. An alpha user reporting a bug can read it
          back to me, and it is the only part of the old screen worth keeping. */}
      {error instanceof Error && error.message && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ mt: 3, maxWidth: 560, wordBreak: 'break-word', fontFamily: 'monospace' }}
        >
          {error.message}
        </Typography>
      )}
    </Box>
  );
}
